import { cache } from "@emotion/css"; // eslint-disable-line @emotion/no-vanilla
import { CacheProvider, Global } from "@emotion/react";
import { NoSsr } from "@mui/base";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { theme } from "@product/scmp-sdk";
import { useLocalStorageValue } from "@react-hookz/web";
import { AnimatePresence, domAnimation, LazyMotion } from "framer-motion";
import { Provider as JotaiProvider } from "jotai";
import type { AppPropsWithLayout } from "next";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { RelayEnvironmentProvider } from "react-relay";
import { getInitialPreloadedQuery, getRelayProps } from "relay-nextjs/app";
import { SWRConfig } from "swr";

import { AdvertisementScripts } from "~/components/advertisement";
import { AppBar } from "~/components/app-bar";
import { AppLinkingData } from "~/components/app-linking-data";
import { IdleQueue } from "~/components/common/idle-queue";
import { PageTransition } from "~/components/common/page-transition";
import { GoogleOneTapLoginContainer } from "~/components/google-one-tap-login";
import { DefaultSeo } from "~/components/meta";
import { TrackingScripts } from "~/components/tracking";
import { OnePlusXScripts } from "~/components/tracking/one-plus-x";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { globalCss } from "~/lib/styles/global-styles";
import { isNoExternalScripts } from "~/lib/utils";

import { FooterElementID, HeaderBottomContainerElementID } from "./_app/consts";
import {
  DynamicHamburgerMenu,
  DynamicLoginDialog,
  DynamicMixpanelProvider,
  DynamicNewsTicker,
  DynamicPopupContainer,
  DynamicRosettaContextProvider,
  DynamicSdkContextProvider,
  DynamicSimpleHooksRunner,
  DynamicSnackbarProvider,
} from "./_app/dynamics";
import { useDisableScrollRestoration } from "./_app/hooks";
import {
  Content,
  FooterContainer,
  HeaderBottomContainer,
  PageContainer,
  StyledHeader,
} from "./_app/styles";

const clientEnvironment = createClientEnvironment();
const initialPreloadedQuery = getInitialPreloadedQuery({
  createClientEnvironment: () => createClientEnvironment(),
});

const App: FunctionComponent<AppPropsWithLayout> = ({ Component, err, pageProps }) => {
  const relayProps = getRelayProps(pageProps, initialPreloadedQuery);
  const environment = relayProps.preloadedQuery?.environment ?? clientEnvironment;

  useDisableScrollRestoration();

  // This is for clearing up the legacy localstorage
  const { remove } = useLocalStorageValue("app-cache");
  useEffect(() => {
    remove();
  }, [remove]);

  // For supporting layout per page
  const renderLayout = () => {
    const PageComponent = <Component {...pageProps} {...relayProps} err={err} />;
    if (Component.getLayout) {
      return Component.getLayout(PageComponent);
    }

    return (
      <PageContainer $responsivePadding={pageProps.contentConfiguration?.responsivePadding}>
        <IdleQueue>
          <DynamicPopupContainer />
          <DynamicHamburgerMenu />
        </IdleQueue>
        <NoSsr>
          <DynamicSimpleHooksRunner />
          <DynamicLoginDialog />
        </NoSsr>
        <AppBar configuration={pageProps?.appBarConfiguration}>
          <NoSsr>
            <IdleQueue>
              <GoogleOneTapLoginContainer />
              <DynamicNewsTicker />
            </IdleQueue>
          </NoSsr>
          <StyledHeader headerConfiguration={pageProps?.headerConfiguration} />
          <HeaderBottomContainer id={HeaderBottomContainerElementID} />
        </AppBar>
        <Content>
          <AnimatePresence initial={false} mode="wait">
            <PageTransition animateKey={pageProps.animateKey}>{PageComponent}</PageTransition>
          </AnimatePresence>
        </Content>
        <FooterContainer id={FooterElementID} />
      </PageContainer>
    );
  };
  return (
    <>
      <AppLinkingData />
      <DefaultSeo />
      {/* Moved to worker thread can't toggle by query params */}
      <OnePlusXScripts />
      <Global styles={globalCss} />
      <LazyMotion features={domAnimation}>
        <MuiThemeProvider theme={theme}>
          <JotaiProvider>
            {!isNoExternalScripts() && (
              <>
                <AdvertisementScripts />
                <TrackingScripts />
              </>
            )}

            <SWRConfig value={{ provider: () => new Map() }}>
              <RelayEnvironmentProvider environment={environment}>
                <DynamicSnackbarProvider>
                  <DynamicMixpanelProvider>
                    <DynamicSdkContextProvider>
                      <DynamicRosettaContextProvider>
                        <CacheProvider value={cache}>{renderLayout()}</CacheProvider>
                      </DynamicRosettaContextProvider>
                    </DynamicSdkContextProvider>
                  </DynamicMixpanelProvider>
                </DynamicSnackbarProvider>
              </RelayEnvironmentProvider>
            </SWRConfig>
          </JotaiProvider>
        </MuiThemeProvider>
      </LazyMotion>
    </>
  );
};

App.displayName = "App";

export default App;
